import React, { useState, useEffect, useMemo } from 'react';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useDevUser } from '../App';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format } from 'date-fns';
import { WASTE_CATEGORIES, getCategoryColor } from '../config/wasteCategories';
import { FaLeaf, FaRecycle, FaCheckCircle, FaClock, FaTrophy, FaMedal, FaStar, FaArrowUp, FaArrowDown, FaSearch, FaFilter, FaTree, FaChartLine, FaMapMarkerAlt } from 'react-icons/fa';
import DashboardMap from '../components/DashboardMap';
import { getCouncilCoordinates } from '../data/ukGeography';
import './IndividualDashboard.css';

const BADGES = [
  { id: 'first_report', name: 'First Steps', icon: FaStar, threshold: 1, color: '#f39c12' },
  { id: 'eco_warrior', name: 'Eco Warrior', icon: FaMedal, threshold: 10, color: '#3498db' },
  { id: 'guardian', name: 'Guardian', icon: FaTrophy, threshold: 50, color: '#9b59b6' },
  { id: 'champion', name: 'Champion', icon: FaTrophy, threshold: 100, color: '#e74c3c' },
];

const IndividualDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const { devUser } = useDevUser() || {};
  const activeUser = devUser || currentUser;
  const [stats, setStats] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(6);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const mapCenter = useMemo(() => {
    const pref = userProfile?.locationPreference || userProfile?.location_preference;
    if (pref?.council) {
      const coords = getCouncilCoordinates(pref.council);
      if (coords) return [coords.lat, coords.lng];
    }
    if (userLocation) return [userLocation.lat, userLocation.lng];
    return [51.5074, -0.1278];
  }, [userProfile, userLocation]);

  const nearbyReports = useMemo(() => {
    const pref = userProfile?.locationPreference || userProfile?.location_preference;
    const [centerLat, centerLng] = mapCenter;
    
    return allReports.filter(report => {
      try {
        const loc = typeof report.location === 'string' ? JSON.parse(report.location) : report.location;
        if (!loc || !loc.lat || !loc.lng) return false;
        
        if (pref?.type === 'realtime' && userLocation) {
          const distance = Math.sqrt(
            Math.pow((loc.lat - userLocation.lat) * 111, 2) + 
            Math.pow((loc.lng - userLocation.lng) * 111 * Math.cos(userLocation.lat * Math.PI / 180), 2)
          );
          return distance < 25;
        }
        
        if (pref?.council && loc.council) {
          return loc.council === pref.council;
        }
        if (pref?.region && loc.region) {
          return loc.region === pref.region;
        }
        if (pref?.ukCountry && loc.country) {
          return loc.country === pref.ukCountry;
        }
        
        const distance = Math.sqrt(
          Math.pow((loc.lat - centerLat) * 111, 2) + 
          Math.pow((loc.lng - centerLng) * 111 * Math.cos(centerLat * Math.PI / 180), 2)
        );
        return distance < 50;
      } catch {
        return false;
      }
    });
  }, [allReports, userProfile, mapCenter, userLocation]);

  useEffect(() => {
    const pref = userProfile?.locationPreference || userProfile?.location_preference;
    if (pref?.type === 'realtime') {
      navigator.geolocation?.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => console.log('Could not get location')
      );
    }
  }, [userProfile]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, reportsData] = await Promise.all([
          api.getDashboardStats(timeRange, activeUser?.uid),
          api.getWasteReports()
        ]);
        setStats(statsData);
        setAllReports(reportsData || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setAllReports([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeRange, activeUser]);

  const getFilteredReports = () => {
    return allReports.filter(report => {
      if (statusFilter !== 'all' && report.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && report.category !== categoryFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesType = report.wasteType?.toLowerCase().includes(query);
        const matchesCategory = report.category?.toLowerCase().includes(query);
        const matchesDescription = report.description?.toLowerCase().includes(query);
        if (!matchesType && !matchesCategory && !matchesDescription) return false;
      }
      return true;
    });
  };

  const filteredReports = getFilteredReports();

  const getUserBadges = () => {
    if (!stats?.userStats) return [];
    const reports = stats.userStats.totalReports;
    return BADGES.filter(badge => reports >= badge.threshold);
  };

  const getNextBadge = () => {
    if (!stats?.userStats) return BADGES[0];
    const reports = stats.userStats.totalReports;
    return BADGES.find(badge => reports < badge.threshold) || null;
  };

  const formatTrendData = () => {
    if (!stats?.trends) return [];
    return stats.trends.map(t => ({
      month: format(new Date(t.month), 'MMM'),
      total: t.total,
      cleaned: t.cleaned,
      pending: t.total - t.cleaned
    }));
  };

  const formatCategoryData = () => {
    if (!stats?.categories) return [];
    return stats.categories.map(c => ({
      name: WASTE_CATEGORIES[c.category]?.name || c.category,
      count: c.count,
      fill: getCategoryColor(c.category)
    }));
  };

  const formatSubcategoryData = () => {
    if (!stats?.subcategories) return [];
    return stats.subcategories.slice(0, 10).map(s => ({
      name: s.wasteType,
      count: s.count,
      category: s.category
    }));
  };

  if (loading) {
    return (
      <div className="individual-dashboard">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const earnedBadges = getUserBadges();
  const nextBadge = getNextBadge();
  const trendData = formatTrendData();
  const categoryData = formatCategoryData();
  const subcategoryData = formatSubcategoryData();

  return (
    <div className="individual-dashboard">
      <div className="dashboard-header-section">
        <div className="header-content">
          <h1><FaLeaf className="header-icon" /> Individual Dashboard</h1>
          <p>Track your environmental impact and community contributions</p>
          {devUser && (
            <div className="dev-user-indicator">
              Viewing as: <strong>{devUser.displayName}</strong>
            </div>
          )}
        </div>
        <div className="header-controls">
          <div className="time-select-wrapper">
            <label className="time-select-label">Select Dashboard Period</label>
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="time-select"
            >
              <option value={1}>Last Month</option>
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months</option>
              <option value={12}>Last 12 Months</option>
              <option value={24}>Last 24 Months</option>
            </select>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <section className="personal-stats-section">
          <h2><FaStar /> Your Impact</h2>
          <div className="personal-stats-grid">
            <div className="personal-stat-card primary">
              <div className="stat-number">{stats?.userStats?.totalReports || 0}</div>
              <div className="stat-label">Reports Submitted</div>
              <FaRecycle className="stat-bg-icon" />
            </div>
            <div className="personal-stat-card success">
              <div className="stat-number">{stats?.userStats?.cleanedReports || 0}</div>
              <div className="stat-label">Cleaned Up</div>
              <FaCheckCircle className="stat-bg-icon" />
            </div>
            <div className="personal-stat-card accent">
              <div className="stat-number">{stats?.userStats?.estimatedImpact || 0} kg</div>
              <div className="stat-label">Waste Removed</div>
              <FaLeaf className="stat-bg-icon" />
            </div>
          </div>

          <div className="badges-section">
            <h3>Your Badges</h3>
            <div className="badges-grid">
              {earnedBadges.length > 0 ? (
                earnedBadges.map(badge => (
                  <div key={badge.id} className="badge-item earned" style={{ borderColor: badge.color }}>
                    <badge.icon style={{ color: badge.color }} />
                    <span>{badge.name}</span>
                  </div>
                ))
              ) : (
                <p className="no-badges">Submit your first report to earn badges!</p>
              )}
            </div>
            {nextBadge && (
              <div className="next-badge-hint">
                <FaClock /> Next: <strong>{nextBadge.name}</strong> - {nextBadge.threshold - (stats?.userStats?.totalReports || 0)} more reports needed
              </div>
            )}
          </div>
        </section>

        <section className="map-section">
          <h2><FaMapMarkerAlt /> Waste Reports Near You</h2>
          <DashboardMap 
            reports={nearbyReports}
            center={mapCenter}
            zoom={12}
            showFilters={true}
            title="My Area"
          />
        </section>

        <section className="leaderboard-section">
          <h2><FaTrophy /> Top Contributors This Month</h2>
          <div className="leaderboard-list">
            {stats?.leaderboard?.length > 0 ? (
              stats.leaderboard.map((user, index) => (
                <div key={index} className={`leaderboard-item ${index < 3 ? 'top-three' : ''}`}>
                  <div className="rank">
                    {index === 0 && <FaTrophy className="gold" />}
                    {index === 1 && <FaMedal className="silver" />}
                    {index === 2 && <FaMedal className="bronze" />}
                    {index > 2 && <span>{index + 1}</span>}
                  </div>
                  <div className="user-info">
                    <span className="name">{user.name}</span>
                  </div>
                  <div className="reports-count">{user.reports} reports</div>
                </div>
              ))
            ) : (
              <p className="no-data">No contributors yet this month</p>
            )}
          </div>
        </section>

        <section className="overall-stats-section">
          <h2><FaChartLine /> Community Overview</h2>
          <div className="overall-stats-grid">
            <div className="overall-stat">
              <span className="value">{stats?.overall?.total || 0}</span>
              <span className="label">Total Reports</span>
            </div>
            <div className="overall-stat cleaned">
              <span className="value">{stats?.overall?.cleaned || 0}</span>
              <span className="label">Cleaned</span>
            </div>
            <div className="overall-stat pending">
              <span className="value">{stats?.overall?.pending || 0}</span>
              <span className="label">Pending</span>
            </div>
          </div>
        </section>

        <section className="trends-section">
          <h2><FaChartLine /> Waste Reports Over Time</h2>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="total" stackId="1" stroke="#3498db" fill="#3498db" fillOpacity={0.6} name="Total Reports" />
                <Area type="monotone" dataKey="cleaned" stackId="2" stroke="#2ecc71" fill="#2ecc71" fillOpacity={0.6} name="Cleaned" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No trend data available</p>
          )}
        </section>

        <section className="category-trends-section">
          <h2>Category Trends This Month</h2>
          <div className="category-trends-grid">
            {stats?.categoryTrends?.map((trend, index) => (
              <div key={index} className="category-trend-card">
                <div className="trend-category" style={{ color: getCategoryColor(trend.category) }}>
                  {WASTE_CATEGORIES[trend.category]?.name || trend.category}
                </div>
                <div className={`trend-change ${trend.change >= 0 ? 'up' : 'down'}`}>
                  {trend.change >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                  {Math.abs(trend.change)}%
                </div>
                <div className="trend-numbers">
                  {trend.thisMonth} reports (was {trend.lastMonth})
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="category-chart-section">
          <h2>Waste by Category</h2>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip />
                <Bar dataKey="count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No category data available</p>
          )}
        </section>

        <section className="subcategory-chart-section">
          <h2>Top Waste Types</h2>
          {subcategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={subcategoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={180} />
                <Tooltip />
                <Bar dataKey="count" fill="#3498db" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No subcategory data available</p>
          )}
        </section>

        <section className="environmental-impact-section">
          <h2><FaTree /> Environmental Impact</h2>
          <div className="impact-cards">
            <div className="impact-card">
              <FaRecycle className="impact-icon" />
              <div className="impact-value">~{stats?.environmentalImpact?.estimatedWasteRemoved || 0} kg</div>
              <div className="impact-label">Estimated Waste Removed</div>
            </div>
            <div className="impact-card">
              <FaTree className="impact-icon green" />
              <div className="impact-value">~{stats?.environmentalImpact?.treesEquivalent || 0}</div>
              <div className="impact-label">Trees Equivalent Saved</div>
            </div>
          </div>
        </section>

        <section className="gallery-section">
          <h2><FaCheckCircle /> Before & After Gallery</h2>
          <p className="gallery-description">See the impact of community cleanups</p>
          {stats?.cleanedGallery?.length > 0 ? (
            <div className="gallery-grid">
              {stats.cleanedGallery.map((item, index) => (
                <div 
                  key={index} 
                  className="gallery-item"
                  onClick={() => setSelectedGalleryItem(item)}
                >
                  <div className="gallery-images">
                    <div className="before-image">
                      {item.beforePhoto ? (
                        <img src={item.beforePhoto} alt="Before cleanup" />
                      ) : (
                        <div className="no-image">No before image</div>
                      )}
                      <span className="image-label">Before</span>
                    </div>
                    <div className="after-image">
                      {item.afterPhoto ? (
                        <img src={item.afterPhoto} alt="After cleanup" />
                      ) : (
                        <div className="no-image">No after image</div>
                      )}
                      <span className="image-label">After</span>
                    </div>
                  </div>
                  <div className="gallery-info">
                    <span className="waste-type">{item.wasteType}</span>
                    {item.cleanupDate && (
                      <span className="cleanup-date">Cleaned: {format(new Date(item.cleanupDate), 'MMM dd, yyyy')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No cleanup evidence available yet</p>
          )}
        </section>

        <section className="filters-section">
          <h2><FaFilter /> Filter & Search Reports</h2>
          <div className="filters-grid">
            <div className="filter-group">
              <label><FaSearch /> Search</label>
              <input 
                type="text"
                placeholder="Search by type or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="cleaned">Cleaned</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All Categories</option>
                {Object.keys(WASTE_CATEGORIES).map(key => (
                  <option key={key} value={key}>{WASTE_CATEGORIES[key].name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filtered-results">
            <div className="results-header">
              <span className="results-count">{filteredReports.length} reports found</span>
              {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && (
                <button 
                  className="clear-filters-btn"
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); setCategoryFilter('all'); }}
                >
                  Clear Filters
                </button>
              )}
            </div>
            <div className="reports-list">
              {filteredReports.slice(0, 10).map((report) => (
                <div 
                  key={report.id} 
                  className={`report-item ${report.status}`}
                  style={{ borderLeftColor: getCategoryColor(report.category) }}
                >
                  <div className="report-main">
                    <span className="report-type">{report.wasteType}</span>
                    <span className="report-category">{WASTE_CATEGORIES[report.category]?.name}</span>
                  </div>
                  <div className="report-meta">
                    <span className={`status-badge ${report.status}`}>
                      {report.status === 'cleaned' ? <FaCheckCircle /> : <FaClock />}
                      {report.status}
                    </span>
                    <span className="report-date">{format(new Date(report.timestamp), 'MMM dd, yyyy')}</span>
                  </div>
                </div>
              ))}
              {filteredReports.length > 10 && (
                <p className="more-results">Showing 10 of {filteredReports.length} reports</p>
              )}
              {filteredReports.length === 0 && (
                <p className="no-results">No reports match your filters</p>
              )}
            </div>
          </div>
        </section>
      </div>

      {selectedGalleryItem && (
        <div className="gallery-modal-overlay" onClick={() => setSelectedGalleryItem(null)}>
          <div className="gallery-modal" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedGalleryItem(null)}>&times;</button>
            <h3>{selectedGalleryItem.wasteType}</h3>
            <div className="modal-images">
              <div className="modal-image-container">
                <h4>Before</h4>
                {selectedGalleryItem.beforePhoto ? (
                  <img src={selectedGalleryItem.beforePhoto} alt="Before" />
                ) : (
                  <div className="no-image">No image available</div>
                )}
                <p>Reported: {format(new Date(selectedGalleryItem.reportDate), 'MMM dd, yyyy')}</p>
              </div>
              <div className="modal-image-container">
                <h4>After</h4>
                {selectedGalleryItem.afterPhoto ? (
                  <img src={selectedGalleryItem.afterPhoto} alt="After" />
                ) : (
                  <div className="no-image">No image available</div>
                )}
                {selectedGalleryItem.cleanupDate && (
                  <p>Cleaned: {format(new Date(selectedGalleryItem.cleanupDate), 'MMM dd, yyyy')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualDashboard;
